import { useMemo, useState } from 'react';
import { Scale, Sparkles, Info } from 'lucide-react';

const eur = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} €`);
const pct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)} %`);
const parseFR = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
};
// Vert si positif, orange si négatif, rouge à partir de -10 %.
const tone = (d) => (d == null ? 'none' : d >= 0 ? 'green' : d > -10 ? 'orange' : 'red');
const TONE_CLS = { green: 'bg-emerald-100 text-emerald-700', orange: 'bg-orange-100 text-orange-700', red: 'bg-red-100 text-red-700', none: 'bg-slate-100 text-slate-500' };
const Delta = ({ d, testId }) => <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold font-mono ${TONE_CLS[tone(d)]}`} data-testid={testId}>{pct(d)}</span>;

const monthsOpen = (c, now) => { const d = parseFR(c.date_contrat); return d ? (now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth() : null; };

export const buildPerformance = (corners, runRateAnnual, now = new Date()) => {
  const yr = now.getFullYear();
  const cutNew = new Date(yr, now.getMonth() - 12, now.getDate());
  const lfl = [], neu = [];
  let hors = 0;
  corners.forEach(c => {
    const rr = runRateAnnual(c);
    if (rr == null) return;
    const open = parseFR(c.date_contrat);
    const base = { c, rr, bm: c.nom_bm || '— Non renseigné', dr: c.nom_dr || '— Non renseigné', mois: monthsOpen(c, now) };
    if (open && open >= cutNew) {
      const ref = c.previ_ca_annuel_ht || null;
      neu.push({ ...base, ref, d: ref ? (rr - ref) / ref * 100 : null });
    } else if (c.ca_n1 > 0 && (!open || open <= new Date(yr - 1, 0, 1))) {
      const ref = c.ca_n1 / 1.1;
      lfl.push({ ...base, ref, d: (rr - ref) / ref * 100 });
    } else hors += 1;
  });
  return { lfl, neu, hors };
};

const summarize = (rows) => {
  const withRef = rows.filter(r => r.ref);
  const rr = withRef.reduce((s, r) => s + r.rr, 0), ref = withRef.reduce((s, r) => s + r.ref, 0);
  const counts = { green: 0, orange: 0, red: 0, none: 0 };
  rows.forEach(r => { counts[tone(r.d)] += 1; });
  return { rr, rrAll: rows.reduce((s, r) => s + r.rr, 0), nRef: withRef.length, ref, ecart: rr - ref, d: ref ? (rr - ref) / ref * 100 : null, counts, n: rows.length };
};

const Summary = ({ title, icon: Icon, s, refLabel, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:bg-slate-900 dark:border-slate-700" data-testid={testId}>
    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500"><Icon className="h-4 w-4 text-orange-500" />{title} · {s.n} corner{s.n > 1 ? 's' : ''}</p>
    <div className="mt-3 flex flex-wrap items-end gap-x-8 gap-y-2">
      <div><p className="text-xs text-slate-500">Réalisé (run rate HT)</p><p className="text-2xl font-bold font-mono">{eur(s.rrAll)}</p></div>
      <div><p className="text-xs text-slate-500">{refLabel}</p><p className="text-2xl font-bold font-mono text-slate-500">{eur(s.ref || null)}</p></div>
      <div><p className="text-xs text-slate-500">Écart</p><p className={`text-2xl font-bold font-mono ${s.ecart >= 0 ? 'text-emerald-600' : s.d > -10 ? 'text-orange-600' : 'text-red-600'}`}>{s.ref ? `${s.ecart > 0 ? '+' : ''}${eur(s.ecart)}` : '—'}</p></div>
      <Delta d={s.d} testId={`${testId}-pct`} />
    </div>
    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">{s.counts.green} au-dessus</span>
      <span className="rounded-full bg-orange-50 px-2.5 py-1 text-orange-700">{s.counts.orange} légèrement en dessous</span>
      <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-700">{s.counts.red} à -10 % et plus</span>
      {s.counts.none > 0 && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">{s.counts.none} sans prévi · écart calculé sur {s.nRef} corner{s.nRef > 1 ? 's' : ''}</span>}
    </div>
  </div>
);

const GroupTable = ({ rows, by }) => {
  const groups = Object.entries(rows.reduce((a, r) => { (a[r[by]] ||= []).push(r); return a; }, {}))
    .map(([k, v]) => ({ k, ...summarize(v) })).sort((a, b) => (a.d ?? 999) - (b.d ?? 999));
  return (
    <table className="w-full text-sm" data-testid={`perf-group-${by}`}>
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500"><th className="py-2">{by === 'dr' ? 'DR' : 'BM'}</th><th className="text-right">Corners</th><th className="text-right">Réalisé</th><th className="text-right">Référence</th><th className="text-right">Écart</th><th className="text-right">%</th></tr></thead>
      <tbody>{groups.map(g => (
        <tr key={g.k} className="border-t border-slate-100 dark:border-slate-800">
          <td className="py-2 font-semibold">{g.k}</td><td className="text-right">{g.n}</td><td className="text-right font-mono">{eur(g.rrAll)}</td>
          <td className="text-right font-mono text-slate-500">{eur(g.ref || null)}</td><td className="text-right font-mono">{g.ref ? eur(g.ecart) : '—'}</td><td className="text-right"><Delta d={g.d} /></td>
        </tr>))}</tbody>
    </table>
  );
};

const DetailTable = ({ rows, refLabel, onRowClick, canEdit, updateCornerField, showPrevi }) => (
  <div className="max-h-[520px] overflow-auto">
    <table className="w-full text-sm min-w-[860px]">
      <thead className="sticky top-0 bg-white dark:bg-slate-900"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
        <th className="py-2">Corner</th><th>BM</th><th>DR</th><th className="text-right">{showPrevi ? 'Ouvert depuis' : 'Ouverture'}</th><th className="text-right">Réalisé (run rate)</th><th className="text-right">{refLabel}</th><th className="text-right">Écart €</th><th className="text-right">Écart %</th></tr></thead>
      <tbody>{[...rows].sort((a, b) => (a.d ?? 999) - (b.d ?? 999)).map(r => (
        <tr key={r.c.id} className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer dark:border-slate-800" onClick={() => onRowClick(r.c)} data-testid={`perf-row-${r.c.id}`}>
          <td className="py-2 font-semibold">{r.c.name_powerbi || r.c.name_sf}<span className="block text-xs font-normal text-slate-500">{r.c.enseigne} {r.c.city}</span></td>
          <td className="text-slate-600">{r.bm}</td><td className="text-slate-600">{r.dr}</td>
          <td className="text-right text-slate-600">{showPrevi ? `${r.mois ?? '—'} mois${r.mois != null && r.mois < 3 ? ' · peu fiable' : ''}` : (r.c.date_contrat || '—')}</td>
          <td className="text-right font-mono">{eur(r.rr)}</td>
          <td className="text-right font-mono text-slate-500" onClick={e => showPrevi && e.stopPropagation()}>
            {showPrevi && canEdit ? <input type="number" min="0" defaultValue={r.ref ?? ''} placeholder="Prévi à saisir" className="w-32 rounded border border-slate-200 px-2 py-1 text-right text-sm dark:bg-slate-800 dark:border-slate-700"
              onBlur={e => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== (r.ref ?? null)) updateCornerField(r.c.id, { previ_ca_annuel_ht: v }); }} data-testid={`perf-previ-${r.c.id}`} /> : eur(r.ref)}
          </td>
          <td className="text-right font-mono">{r.ref ? eur(r.rr - r.ref) : '—'}</td><td className="text-right"><Delta d={r.d} /></td>
        </tr>))}</tbody>
    </table>
  </div>
);

export const PerformanceTab = ({ corners, runRateAnnual, onRowClick, canEdit, updateCornerField }) => {
  const [view, setView] = useState('lfl');
  const [by, setBy] = useState('detail');
  const { lfl, neu, hors } = useMemo(() => buildPerformance(corners, runRateAnnual), [corners, runRateAnnual]);
  const sL = summarize(lfl), sN = summarize(neu);
  const rows = view === 'lfl' ? lfl : neu;
  const refLabel = view === 'lfl' ? 'CA N-1 HT' : 'Prévi CA annuel HT';
  const pill = (on) => `rounded-full px-3 py-1.5 text-xs font-semibold ${on ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`;
  return (
    <div className="space-y-4" data-testid="perf-tab">
      <div className="grid gap-4 lg:grid-cols-2">
        <button type="button" className="text-left" onClick={() => setView('lfl')}><Summary title="Like for like" icon={Scale} s={sL} refLabel="CA N-1 HT" testId="perf-summary-lfl" /></button>
        <button type="button" className="text-left" onClick={() => setView('new')}><Summary title="Nouveaux contrats (< 12 mois)" icon={Sparkles} s={sN} refLabel="Prévi de vente" testId="perf-summary-new" /></button>
      </div>
      <p className="flex items-start gap-2 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Like for like : corners ouverts avant le 1er janvier N-1 et ayant un CA N-1, run rate N comparé au CA N-1 ramené en HT. Nouveaux contrats : 1er contrat il y a moins de 12 mois, run rate comparé au Prévi CA annuel HT saisi. Vert = au-dessus, orange = en dessous, rouge = -10 % et plus. {hors > 0 && `${hors} corner(s) hors périmètre (ouverts en cours d'année N-1 ou sans CA N-1).`}</p>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:bg-slate-900 dark:border-slate-700">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button type="button" className={pill(view === 'lfl')} onClick={() => setView('lfl')} data-testid="perf-view-lfl">Like for like</button>
          <button type="button" className={pill(view === 'new')} onClick={() => setView('new')} data-testid="perf-view-new">Nouveaux contrats</button>
          <span className="mx-2 h-5 w-px bg-slate-200" />
          {[['detail', 'Par corner'], ['bm', 'Par BM'], ['dr', 'Par DR']].map(([k, l]) => <button key={k} type="button" className={pill(by === k)} onClick={() => setBy(k)} data-testid={`perf-by-${k}`}>{l}</button>)}
        </div>
        {rows.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">Aucun corner dans ce périmètre.</p>
          : by === 'detail' ? <DetailTable rows={rows} refLabel={refLabel} onRowClick={onRowClick} canEdit={canEdit} updateCornerField={updateCornerField} showPrevi={view === 'new'} />
            : <GroupTable rows={rows} by={by} />}
      </div>
    </div>
  );
};
